import { serverHost } from "variables/serverdata";
import React from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from "reactstrap";

// Fetch a single tenant by UUID
export const getTenant = async (tenant_uuid) => {
    const res = await fetch(`${serverHost}/tenants/get_tenant/${tenant_uuid}`);
    if (!res.ok) throw new Error("Failed to fetch tenant");
    return res.json();
};

// Fetch all tenants for a site
export const getTenants = async (site_uuid) => {
    const res = await fetch(
        `${serverHost}/hrms/get_tenants/${encodeURIComponent(site_uuid)}`
    );

    if (!res.ok) {
        throw new Error("Failed to fetch tenants");
    }

    return res.json();
};

// Fetch tenant summary counts
export const getTenantsSummary = async (site_uuid) => {
    const res = await fetch(`${serverHost}/hrms/get_tenants_summary/${site_uuid}`);

    const data = await res.json().catch(() => ({}));

    if (!res.ok) {
        throw new Error(data.error || "Failed to fetch tenant summary");
    }

    return data;
};

// Create a tenant
export const createTenant = async (tenantData) => {
    const res = await fetch(`${serverHost}/hrms/create_tenant`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(tenantData),
    });

    if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || "Failed to create tenant");
    }

    return res.json();
};

// Update a tenant
export const updateTenant = async (tenant_uuid, tenantData) => {
    const res = await fetch(`${serverHost}/hrms/update_tenant/${tenant_uuid}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(tenantData),
    });

    if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || "Failed to update tenant");
    }

    return res.json();
};

// Delete a tenant
export const deleteTenant = async (tenant_uuid) => {
    const res = await fetch(`${serverHost}/hrms/delete_tenant/${tenant_uuid}`, {
        method: "DELETE"
    });

    if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || "Failed to delete tenant");
    }

    return res.json();
};

// Bulk delete tenants
export const deleteTenants = async (tenant_uuids) => {
    const res = await fetch(`${serverHost}/hrms/delete_tenants`, {
        method: "DELETE",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify({ tenant_uuids }),
    });

    if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || "Failed to delete tenants");
    }

    return res.json();
};

// Fetch User
export const getUser = () => {
    const user = JSON.parse(localStorage.getItem("user")) || {};
    return {
        user_uuid: user.user_uuid || null,
    };
};

// Fetch Site
export const getSite = () => {
    const site = JSON.parse(localStorage.getItem("selectedSite")) || {};
    return {
        site_uuid: site.site_uuid || null,
    };
};

// Delete Tenant Modal
export function DeleteTenantModal({ isOpen, onConfirm, onCancel, tenantName }) {
    return (
        <Modal isOpen={isOpen} toggle={onCancel} centered>
            <ModalHeader toggle={onCancel} className="text-danger">
                Delete Tenant
            </ModalHeader>
            <ModalBody>
                Are you sure you want to delete <strong>{tenantName}</strong>? This action cannot be undone.
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" onClick={onCancel}>
                    Cancel
                </Button>
                <Button color="danger" onClick={onConfirm}>
                    Delete
                </Button>
            </ModalFooter>
        </Modal>
    );
}

export const digitsOnly = (value = "") => String(value).replace(/\D/g, "");

export const countDigits = (value = "") => String(value).replace(/\D/g, "").length;

export const hasTooManyDigits = (value = "", maxDigits = 11) => {
    return countDigits(value) > maxDigits;
};

export const isValidPhone = (value = "", { required = false, maxDigits = 11 } = {}) => {
    const raw = String(value ?? "").trim();

    if (!raw) return !required;

    return !hasTooManyDigits(raw, maxDigits);
};

export const isValidPin = (value = "") => {
    const raw = String(value ?? "").trim();

    if (!raw) return true; // optional

    return /^\d{2,15}$/.test(raw);
};

export const formatPhone = (value = "") => {
    const raw = String(value ?? "").trim();
    if (!raw) return "-";

    if (!/^\d+$/.test(raw)) {
        return raw;
    }

    if (raw.length === 11) {
        return `${raw[0]}-${raw.slice(1, 4)}-${raw.slice(4, 7)}-${raw.slice(7)}`;
    }

    if (raw.length === 10) {
        return `${raw.slice(0, 3)}-${raw.slice(3, 6)}-${raw.slice(6)}`;
    }

    return raw;
};

export const normalizePhoneForSave = (value = "") => {
    const raw = String(value ?? "").trim();
    if (!raw) return "";

    const digits = raw.replace(/\D/g, "");

    if (digits.length > 11) {
        throw new Error("Phone value cannot contain more than 11 digits.");
    }

    if (digits.length === 10 || digits.length === 11) {
        return digits;
    }

    return raw;
};

export const normalizePinForSave = (value = "") => {
    const raw = String(value ?? "").trim();

    if (!raw) return "";

    if (!/^\d{2,15}$/.test(raw)) {
        throw new Error("PIN must be 2 to 15 digits.");
    }

    return raw;
};

// Fetch all devices for a site
export const getDevices = async (site_uuid) => {
    const res = await fetch(`${serverHost}/hrms/get_devices/${site_uuid}`);
    if (!res.ok) throw new Error("Failed to fetch devices");
    return res.json();
};

// Create visitor pass
export const createVisitorPass = async (payload) => {
    const res = await fetch(`${serverHost}/hrms/visitor_pass/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
    });

    if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || "Failed to create visitor pass");
    }

    return res.json();
};

export const emailVisitorPass = async (visitor_pass_uuid) => {
    const res = await fetch(`${serverHost}/hrms/visitor_pass/email/${visitor_pass_uuid}`, {
        method: "POST",
    });

    if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || "Failed to email visitor pass");
    }

    return res.json();
};

export const syncSaltoTenants = async (site_uuid) => {
    const res = await fetch(`${serverHost}/hrms/salto/sync/${site_uuid}`, {
        method: "POST",
    });

    const data = await res.json().catch(() => ({}));

    if (!res.ok) {
        throw new Error(data.error || "Failed to sync Salto tenants");
    }

    return data;
};

export const syncDevice = async (device_uuid, site_uuid) => {
    const res = await fetch(`${serverHost}/hrms/sync_device/${device_uuid}?site_uuid=${site_uuid}`, {
        method: "POST",
    });

    const data = await res.json().catch(() => ({}));

    if (!res.ok) {
        throw new Error(data.error || data.detail || "Failed to sync device");
    }

    if (data.success === false) {
        throw new Error(
            data.errors?.[0]?.error ||
            data.error ||
            "Device sync failed"
        );
    }

    return data;
};

export const getVisitorPasses = async (site_uuid) => {
    const res = await fetch(`${serverHost}/hrms/visitor_passes/${site_uuid}`);

    if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || "Failed to fetch visitor passes");
    }

    return res.json();
};

export const revokeVisitorPass = async (visitor_pass_uuid) => {
    const res = await fetch(`${serverHost}/hrms/visitor_pass/revoke/${visitor_pass_uuid}`, {
        method: "POST",
    });

    if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || "Failed to revoke visitor pass");
    }

    return res.json();
};

export const deleteVisitorPass = async (visitor_pass_uuid) => {
    const res = await fetch(`${serverHost}/hrms/visitor_pass/delete/${visitor_pass_uuid}`, {
        method: "DELETE",
    });

    if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || "Failed to delete visitor pass");
    }

    return res.json();
};

export const deleteVisitorPasses = async (visitor_pass_uuids) => {
    const res = await fetch(`${serverHost}/hrms/visitor_pass/delete_many`, {
        method: "DELETE",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify({ visitor_pass_uuids }),
    });

    if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || "Failed to delete visitor passes");
    }

    return res.json();
};

export const getLastSaltoSync = async (site_uuid) => {
    const res = await fetch(`${serverHost}/hrms/salto/last_sync/${site_uuid}`);

    const data = await res.json().catch(() => ({}));

    if (!res.ok) {
        throw new Error(data.error || "Failed to fetch last Salto sync");
    }

    return data;
};